import { filterClasses } from "@swivl-mono/helpers";
import { ButtonOption, ButtonsData, type DebugLevel, StructuredActionTaken, debugLevelAtLeast } from "@swivl-mono/types";
import React from "react";

import { formatTime } from "../../Helpers/formatTime";
import type { ChatMessage } from "../../Types/BotChat.Types";

interface ButtonsProps {
  data: ButtonsData;
  message: ChatMessage;
  /** Called when the user taps a button. Wired through useChat → sendStructuredAction. */
  onAction?: (action: StructuredActionTaken) => void;
  /** Read-only mode for the agent timeline. No tap handlers; renders selected state if actionTaken set. */
  readOnly?: boolean;
  /**
   * Debug visibility level. At `agent` or `admin`, shows each option's
   * `llmMessage` as a muted secondary line ("→ LLM saw: ..."). At `none`
   * (user widget default), `llmMessage` is never in the DOM.
   */
  debugLevel?: DebugLevel;
}

/**
 * `buttons` structured-message renderer.
 *
 * Differs from picker kinds: each option carries its own `llmMessage`
 * (see ButtonOption). Tap → fires `onAction` with `selected` + the
 * picked option's `id`. The buttons message stays in the timeline with
 * the selected option highlighted; no separate user-bubble dispatched
 * (the next-turn LLM transcript synthesizes the picked option's
 * `llmMessage` server-side).
 *
 * Layout follows the Chat Embed Figma: horizontal flex-wrap with each
 * button growing to fill its row. Wraps to multiple lines when needed.
 *
 * See docs/snowy/plans/2026-04-28-buttons-kind-plan.md §2 + §7 Stage 3.
 */
export function Buttons({ data, message, onAction, readOnly = false, debugLevel = "none" }: ButtonsProps): React.ReactElement {
  const action = message.actionTaken as StructuredActionTaken | undefined;
  const selectedId = action?.type === "selected" && typeof action.target === "string" ? action.target : null;
  const dismissed = action?.type === "dismissed";
  const showLlmMessage = debugLevelAtLeast(debugLevel, "agent");

  const handleTap = (option: ButtonOption): void => {
    if (readOnly || selectedId || dismissed) return;
    onAction?.({
      type: "selected",
      takenAt: new Date().toISOString(),
      target: option.id,
      snapshot: option
    });
  };

  // User view, post-selection: render only the picked option as a
  // standalone confirmation tile. Hides the unpicked options that would
  // otherwise sit there as greyed-out dead UI. Agent timeline (readOnly)
  // keeps the full set so reviewers can see what was offered + what was
  // picked. Per Figma node 370-28442 (selected-state design).
  const isUserSelection = !readOnly && selectedId !== null;
  const optionsToRender = isUserSelection ? data.options.filter((o) => o.id === selectedId) : data.options;
  // The selection is the user's own message, so time it from when they tapped.
  const selectionTime = isUserSelection && action?.takenAt ? formatTime(action.takenAt) : "";

  return (
    <div className={filterClasses("rounded-2xl", isUserSelection || readOnly ? "" : "p-3")}>
      {data.prompt && <div className="mb-2 text-sm text-grey-700">{data.prompt}</div>}
      <div className="flex flex-wrap justify-end gap-2">
        {optionsToRender.map((option) => {
          const isSelected = selectedId === option.id;
          const isFaded = (selectedId !== null && !isSelected) || dismissed;
          return (
            <ButtonOptionTile
              key={option.id}
              option={option}
              isSelected={isSelected}
              isFaded={isFaded}
              readOnly={readOnly || selectedId !== null || dismissed}
              // `readOnly` here is the COMPONENT-level flag, set only by the
              // agent console. The tile's own `readOnly` above also goes true in
              // the widget once a customer taps, so it can't drive styling.
              compact={readOnly}
              showLlmMessage={showLlmMessage}
              onTap={() => handleTap(option)}
            />
          );
        })}
      </div>
      {selectionTime && (
        <div className="mt-1 flex justify-end">
          <span className="font-mono text-xs leading-4 text-grey-500">{selectionTime}</span>
        </div>
      )}
    </div>
  );
}

interface ButtonOptionTileProps {
  option: ButtonOption;
  isSelected: boolean;
  isFaded: boolean;
  readOnly: boolean;
  /** Agent-console sizing — see ButtonOptionTile. */
  compact: boolean;
  showLlmMessage: boolean;
  onTap: () => void;
}

function ButtonOptionTile({ option, isSelected, isFaded, readOnly, compact, showLlmMessage, onTap }: ButtonOptionTileProps): React.ReactElement {
  // Compact is the agent console: these are a record of what was offered, not
  // controls, so they're sized as chrome (4px radius, body-sm, tighter padding,
  // a lighter border) instead of shouting like the customer-facing tiles.
  const baseTile = compact
    ? "body-sm flex flex-col items-center justify-center rounded-sm border px-2 py-1.5 text-center transition-colors"
    : "flex flex-col items-center justify-center rounded-lg border p-3 text-center text-base leading-snug transition-colors";

  const stateClasses = isSelected
    ? filterClasses("border-primary-500 bg-primary-500 text-white", !compact && "rounded-br-none")
    : isFaded
      ? // grey-600 in the console: grey-400 was too washed out to read. The
        // widget keeps grey-400 — its faded tiles only appear on a dismissed
        // message, where receding is the point.
        filterClasses("bg-grey-50", compact ? "border-grey-200 text-grey-600" : "border-grey-300 text-grey-400")
      : compact
        ? "border-primary-200 bg-white text-primary-600"
        : "border-primary-500 bg-white text-primary-500 hover:bg-primary-50";

  const labelLine = (
    <span className={filterClasses("flex items-center gap-1.5", compact ? "body-sm-alt" : "font-normal")}>
      {isSelected && <CheckIcon />}
      {option.label}
    </span>
  );

  // Mono in the console ties this to the other debug typography in the panel.
  // Goes white on a selected tile — the muted greys are unreadable on the
  // filled primary background.
  const debugLine = showLlmMessage ? (
    <span
      className={filterClasses(
        compact ? "body-mono mt-0.5" : "mt-1 text-[10px] italic",
        isSelected ? "text-white" : compact ? "text-grey-600" : "text-grey-400"
      )}
    >
      → LLM saw: {option.llmMessage}
    </span>
  ) : null;

  if (readOnly) {
    return (
      // Console only: these read as buttons but can't be pressed here, so the
      // pointer says disabled rather than turning into a text caret.
      <div className={filterClasses(baseTile, stateClasses, compact && "cursor-not-allowed")}>
        {labelLine}
        {debugLine}
      </div>
    );
  }

  return (
    <button type="button" onClick={onTap} className={filterClasses(baseTile, stateClasses, "cursor-pointer")}>
      {labelLine}
      {debugLine}
    </button>
  );
}

function CheckIcon(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 8 7 12 13 4" />
    </svg>
  );
}
